/**
 * Shared menu visibility + flatten helpers for ProjectSidebar and HeaderSearchBox.
 */

import { MENU_STRUCTURE } from '../config/menuStructure';
import { filterMenuStructure } from '../config/menuConfig';
import {
  canAccessResource,
  canAccessSubDepartmentResource,
  getRequiredDepartments,
  getRequiredSubDepartments
} from '../config/resourceAccess';
import { FLAG_GATED_MENU_ITEMS, isFlagAdmin } from './featureFlags';
import { isProposalScheduleAllowed, isExecutionPlanAllowed, isScheduleAnalysisAllowed } from '../config/proposalScheduleAccess';

const LGL_EMAIL_DOMAINS = [
  'laframboise.net',
  'laframboisegroup.com',
  'laframboisegrpltd.onmicrosoft.com'
];

const EXTERNAL_PROGRESS_MENU = new Set([
  'progressTrackers',
  'piping-insulation',
  'mechanical-rotating-eqp',
  'structure',
  'hvac',
  'mechanical-ducts',
  'boilermaker',
  'electrical',
  'civil'
]);

// Accounting opens the extra in Maestro from the Change Log, so cost clerks see
// it even though Change Management is otherwise Construction only.
const COST_CLERK_MENU = new Set(['changeManagement', 'control-chart']);
const COST_CLERK_ROLE = 'accounting_cost_clerk';

function isCostClerk(userPermissions) {
  if (!userPermissions) return false;
  if (userPermissions.roleName === COST_CLERK_ROLE || userPermissions.roleId === COST_CLERK_ROLE) return true;
  const secondary = Array.isArray(userPermissions.secondaryRoles) ? userPermissions.secondaryRoles : [];
  return secondary.some((sr) => sr?.role_id === COST_CLERK_ROLE);
}

export function isExternalEmail(email) {
  const at = String(email || '').lastIndexOf('@');
  const domain = at >= 0 ? String(email).slice(at + 1).trim().toLowerCase() : '';
  return !!domain && !LGL_EMAIL_DOMAINS.includes(domain);
}

/** Find ancestor IDs from immediate parent up to root (or [] if top-level). */
export function findAncestorItemIds(itemId, items = MENU_STRUCTURE, ancestors = []) {
  for (const item of items) {
    if (item.id === itemId) return ancestors;
    if (item.subItems) {
      const found = findAncestorItemIds(itemId, item.subItems, [...ancestors, item.id]);
      if (found !== null) return found;
    }
  }
  return null;
}

/**
 * Department / sub-department gate for a leaf.
 * Mapped leaves use their own list. Unmapped leaves inherit the nearest
 * mapped ancestor (so `schedule-ga-dashboards` follows `schedule` → Construction).
 */
export function canAccessMappedOrAncestor(itemId, userIds, canAccessFn, getRequiredFn) {
  const leafRequired = getRequiredFn(itemId);
  if (leafRequired && leafRequired.length > 0) {
    return canAccessFn(itemId, userIds);
  }
  const ancestors = findAncestorItemIds(itemId) || [];
  for (let i = ancestors.length - 1; i >= 0; i -= 1) {
    const required = getRequiredFn(ancestors[i]);
    if (required && required.length > 0) {
      return canAccessFn(ancestors[i], userIds);
    }
  }
  return true;
}

/**
 * Same three-layer visibility rules as ProjectSidebar.canSeeMenuItem.
 */
export function canSeeMenuItem(itemId, {
  userPermissions,
  permissionsLoaded,
  featureFlags,
  userEmail
} = {}) {
  if (FLAG_GATED_MENU_ITEMS.includes(itemId)) {
    if (!featureFlags || featureFlags[itemId] === false) return false;
  }

  if (itemId === 'development-feature-flags' && !isFlagAdmin(userEmail)) {
    return false;
  }

  // Proposal Schedule / Execution Plan — hard allowlist BEFORE superadmin bypass
  if (itemId === 'estimation-proposal-schedule') {
    return isProposalScheduleAllowed(userEmail);
  }
  if (itemId === 'schedule-execution-plan') {
    return isExecutionPlanAllowed(userEmail);
  }
  if (itemId === 'schedule-analysis') {
    return isScheduleAnalysisAllowed(userEmail);
  }

  // A guest has no LGL department. On a job they are assigned to, the menu
  // is Progress Tracking. Actuals, reports, and the rest stay hidden.
  if (isExternalEmail(userEmail)) {
    if (itemId === 'home' || itemId === 'my-projects') return true;
    return EXTERNAL_PROGRESS_MENU.has(itemId);
  }

  if (itemId === 'home' || itemId === 'my-projects') return true;
  if (itemId === 'actuals') return true;
  if (itemId === 'coding-structure') return true;
  // Hiring / Layoff master list — Accounting + superadmin only
  if (itemId === 'hiring-layoff') {
    if (!permissionsLoaded) return false;
    if (userPermissions?.isSuperadmin) return true;
    return canAccessResource('hiring-layoff', userPermissions?.allDepartmentIds || []);
  }

  // Company-wide HSE Power BI: PM and above (Manager+) + all Safety department users
  if (itemId === 'hse-report') {
    if (!permissionsLoaded) return false;
    if (userPermissions?.isSuperadmin || userPermissions?.isAdmin) return true;
    const auth = userPermissions?.authorityLevel;
    const depts = userPermissions?.allDepartmentIds || [];
    const pmOrAbove = ['System', 'Upper Management', 'Manager'].includes(auth);
    const isSafety = depts.some((id) => Number(id) === 5);
    return pmOrAbove || isSafety;
  }

  if (itemId.startsWith('material-management')) {
    if (!permissionsLoaded) return false;
    if (userPermissions?.isSuperadmin || userPermissions?.isAdmin) return true;
    return canAccessResource(itemId, userPermissions?.allDepartmentIds || []);
  }

  // SharePoint / Autodesk Build / Procore: open to anyone who can select the job/estimate
  // (sidebar requires project/estimate; API enforces assignment). Not department-gated.
  if (
    itemId === 'project-sharepoint' ||
    itemId === 'documentationManagement-sharepoint' || // legacy menu id
    itemId === 'estimate-sharepoint' ||
    itemId === 'project-autodesk-build' ||
    itemId === 'estimate-autodesk-build' ||
    itemId === 'project-procore' ||
    itemId === 'estimate-procore'
  ) {
    return true;
  }

  if (!permissionsLoaded) return false;

  if (userPermissions?.isSuperadmin || userPermissions?.isAdmin) return true;

  if (COST_CLERK_MENU.has(itemId) && isCostClerk(userPermissions)) return true;

  const menuItems = userPermissions?.menuItems || [];
  const ancestors = findAncestorItemIds(itemId) || [];
  const coveredByMenuItems = (id) =>
    menuItems.includes(id) || menuItems.some((mi) => id.startsWith(`${mi}-`));
  const hasMenuAccess =
    menuItems.includes('*') ||
    coveredByMenuItems(itemId) ||
    ancestors.some((ancestorId) => coveredByMenuItems(ancestorId));
  if (!hasMenuAccess) return false;

  const userDeptIds = userPermissions?.allDepartmentIds || [];
  if (!canAccessMappedOrAncestor(itemId, userDeptIds, canAccessResource, getRequiredDepartments)) {
    return false;
  }

  const userSubDeptIds = userPermissions?.subDepartments || [];
  if (!canAccessMappedOrAncestor(
    itemId,
    userSubDeptIds,
    canAccessSubDepartmentResource,
    getRequiredSubDepartments
  )) {
    return false;
  }

  return true;
}

/**
 * Flatten menu into searchable navigable entries (leaves + clickable roots).
 * Searches across all departments (not just the currently selected one).
 */
export function flattenSearchableMenuItems(menuStructure = filterMenuStructure(MENU_STRUCTURE)) {
  const out = [];

  const walk = (items, pathLabels, department) => {
    for (const item of items) {
      if (!item?.id || item.id === 'department-selector') continue;

      const dept = item.department || department || null;
      const labels = [...pathLabels, item.label];
      const hasChildren = Array.isArray(item.subItems) && item.subItems.length > 0;

      if (!hasChildren) {
        out.push({
          id: item.id,
          label: item.label,
          path: labels.join(' → '),
          department: dept,
          requiresProject: !!item.requiresProject,
          requiresEstimate: !!item.requiresEstimate,
          hideInProject: !!item.hideInProject,
          requiresSuperadmin: !!item.requiresSuperadmin,
          icon: item.icon || null
        });
      } else {
        // Parents are not navigable entry points here — children carry the full path
        // so searching "Schedule" still matches "Schedule → Model".
        walk(item.subItems, labels, dept);
      }
    }
  };

  walk(menuStructure, [], null);
  return out;
}

/**
 * Filter flattened items for the current user/context.
 * Unlike the sidebar, does NOT filter by selected department (search is global).
 */
export function getVisibleSearchItems({
  userPermissions,
  permissionsLoaded,
  featureFlags,
  userEmail,
  projectId,
  selectedEstimateId
} = {}) {
  const isSuperadmin = !!(userPermissions?.isSuperadmin || userPermissions?.isAdmin);
  const flat = flattenSearchableMenuItems();

  return flat.filter((item) => {
    if (item.requiresSuperadmin && !isSuperadmin) return false;
    if (item.hideInProject && projectId && !isSuperadmin) return false;
    // Keep project/estimate-gated items visible in search with a hint; navigation
    // already sends users to project/estimate pickers when needed.
    if (!canSeeMenuItem(item.id, {
      userPermissions,
      permissionsLoaded,
      featureFlags,
      userEmail
    })) {
      return false;
    }
    return true;
  }).map((item) => ({
    ...item,
    needsProject: !!(item.requiresProject && !projectId),
    needsEstimate: !!(item.requiresEstimate && !selectedEstimateId)
  }));
}

/** True if any path segment (or word in a segment) starts with q. */
function pathSegmentStartsWith(path, q) {
  const segments = String(path || '')
    .toLowerCase()
    .split(/\s*→\s*/);
  return segments.some((seg) => {
    if (seg.startsWith(q)) return true;
    return seg.split(/[\s/_-]+/).some((word) => word.startsWith(q));
  });
}

export function scoreMenuMatch(item, query) {
  const q = query.trim().toLowerCase();
  if (!q) return 0;
  const label = (item.label || '').toLowerCase();
  const path = (item.path || '').toLowerCase();
  const id = (item.id || '').toLowerCase();
  const idQ = q.replace(/\s+/g, '-');

  // Label matches only — do not treat unrelated labels as hits just because
  // a parent folder name matched (e.g. "Submittals" must not match "schedule").
  if (label === q) return 100;
  if (label.startsWith(q)) return 90;
  if (label.includes(q)) return 75;

  // Path: only segment/word prefixes (Schedule → Planner matches "schedule";
  // "Technical → Submittals" does not).
  if (pathSegmentStartsWith(path, q)) return 55;

  if (id === idQ || id.startsWith(`${idQ}-`) || id.endsWith(`-${idQ}`) || id.includes(`-${idQ}-`)) {
    return 40;
  }

  const tokens = q.split(/\s+/).filter(Boolean);
  if (
    tokens.length > 1
    && tokens.every((t) => label.includes(t) || pathSegmentStartsWith(path, t))
  ) {
    return 50;
  }
  return 0;
}

export function searchMenuItems(items, query, limit = 40) {
  const q = (query || '').trim();
  if (!q) return [];
  // Prefer higher score, then shorter label (tighter match), then path.
  return items
    .map((item) => ({ item, score: scoreMenuMatch(item, q) }))
    .filter((x) => x.score > 0)
    .sort((a, b) => (
      b.score - a.score
      || (a.item.label || '').length - (b.item.label || '').length
      || a.item.path.localeCompare(b.item.path)
    ))
    .slice(0, limit)
    .map((x) => x.item);
}

/** Ask the mounted ProjectSidebar to run its existing click handler. */
export function dispatchMenuNavigate(itemId, { department } = {}) {
  window.dispatchEvent(new CustomEvent('portal:navigateMenu', {
    detail: { itemId, department: department || null }
  }));
}
